import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';
import { localeDate, tz } from '../../utils/region';
import { useStore } from '../../store';
import EmailViewer from '../EmailViewer';
import { pageToolbar, PAGE_INSET } from '../../tabPalette';   // the page toolbar's bubble + the one page gutter (report 76a2c198)

type Account = { id: string; name: string; email: string; color?: string };
type Email = {
  id: string; account_id: string; subject: string; from_name: string; from_email: string;
  date: number; preview?: string; starred?: number; folder?: string;
};

const fmtWhen = (ts: number) => {
  if (!ts) return '';
  const ms = ts < 1e12 ? ts * 1000 : ts;   // tolerate seconds or millis
  return new Date(ms).toLocaleString(localeDate(), { timeZone: tz(), day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false });
};

// ── Dashboard "Starred" — starred emails grouped per mailbox ────────────────
export function StarredView() {
  const { t } = useTranslation();
  const showToast = useStore(s => s.showToast);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [byAccount, setByAccount] = useState<Record<string, Email[]>>({});
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [viewFor, setViewFor] = useState<Email | null>(null);

  const load = async () => {
    setLoading(true); setErr('');
    try {
      const accs: Account[] = await api.getAccounts();
      setAccounts(accs);
      const map: Record<string, Email[]> = {};
      await Promise.all(accs.map(async a => {
        try { const res = await api.getEmails({ folder: 'starred', accountId: a.id }); map[a.id] = res.emails || []; }
        catch { map[a.id] = []; }
      }));
      setByAccount(map);
    } catch (e: any) { setErr(e.message); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const unstar = async (e: Email) => {
    if (!window.confirm(t('st_unstar_confirm'))) return;   // confirm before removing the star
    try {
      await api.patchEmail(e.id, { starred: false });
      setByAccount(prev => ({ ...prev, [e.account_id]: (prev[e.account_id] || []).filter(x => x.id !== e.id) }));
    } catch (er: any) { showToast(er.message); }
  };

  const total = Object.values(byAccount).reduce((s, list) => s + list.length, 0);
  const accentFor = (a: Account) => a.color || 'var(--accent)';

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {/* The page's top bar — the star, the page's name, the count and the refresh button — is a bubble, as every page
          toolbar is (Calem, report 76a2c198, 2nd opening — see pageToolbar): 10px corners and a --border-strong line
          all the way round where a --border line ran under it alone, PAGE_INSET in from the page's edges — the one
          page gutter the whole system stands in by, 15px (that report's 5th opening: "Move the left and right page
          spacing in from the edges to 15 px on every page of the SLF system"; see PAGE_INSET in tabPalette) — with a
          gap above and below it. What is under it had nothing to line up with and ran edge to edge; the mailbox
          headings and the starred rows stand in by that same 15 now, so the page's left and right spacing is the one
          spacing. The per-mailbox headings keep their own sticky strips. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 18px', ...pageToolbar(), background: 'var(--bg-surface)' }}>
        <i className="ti ti-star-filled" style={{ color: '#F5A623', fontSize: 17 }} />
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>{t('mod_starred')}</div>
        <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{total}</span>
        <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)', marginLeft: 6 }}>· {t('st_dblclick')}</span>
        <div style={{ flex: 1 }} />
        <button onClick={load} title={t('refresh')} style={{ width: 32, height: 32, borderRadius: 'var(--radius-md)', border: 'none', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer' }}><i className="ti ti-refresh" style={{ fontSize: 15 }} /></button>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: `8px ${PAGE_INSET}px` }}>
        {err && <div style={{ fontSize: 12, color: '#E24B4A', background: '#FCEBEB', padding: '10px 18px' }}>{err}</div>}
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}><i className="ti ti-loader" style={{ fontSize: 24, color: 'var(--text-tertiary)' }} /></div>
        ) : total === 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 12, color: 'var(--text-tertiary)' }}>
            <i className="ti ti-star" style={{ fontSize: 40 }} />
            <div style={{ fontSize: 14 }}>{t('st_empty')}</div>
          </div>
        ) : (
          // one section per mailbox that has starred emails (kept separate, never mixed)
          accounts.filter(a => (byAccount[a.id] || []).length > 0).map(a => (
            <div key={a.id} style={{ marginBottom: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 18px', position: 'sticky', top: 0, background: 'var(--bg-app)', borderBottom: '1px solid var(--border)', zIndex: 1 }}>
                <span style={{ width: 9, height: 9, borderRadius: '50%', background: accentFor(a), flexShrink: 0 }} />
                <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>{a.name || a.email}</span>
                <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{a.email}</span>
                <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 700, color: 'var(--text-tertiary)', background: 'var(--c-gray-100)', borderRadius: 999, padding: '1px 9px' }}>{(byAccount[a.id] || []).length}</span>
              </div>
              {(byAccount[a.id] || []).map(e => (
                <div key={e.id} onDoubleClick={() => setViewFor(e)} title={t('st_dblclick')}
                  style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '11px 18px', borderBottom: '1px solid var(--border)', cursor: 'pointer', userSelect: 'none' }}
                  onMouseEnter={ev => (ev.currentTarget.style.background = 'var(--bg-active)')} onMouseLeave={ev => (ev.currentTarget.style.background = 'transparent')}>
                  <button onClick={ev => { ev.stopPropagation(); unstar(e); }} title={t('st_unstar')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#F5A623', fontSize: 17, lineHeight: 1, paddingTop: 2 }}><i className="ti ti-star-filled" /></button>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.from_name || e.from_email}</span>
                      <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)', flexShrink: 0 }}>{fmtWhen(e.date)}</span>
                    </div>
                    <div style={{ fontSize: 13, color: 'var(--text-primary)', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.subject || '(no subject)'}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.preview || ''}</div>
                  </div>
                </div>
              ))}
            </div>
          ))
        )}
      </div>

      {viewFor && <EmailViewer emailId={viewFor.id} snapshot={{ subject: viewFor.subject, fromName: viewFor.from_name, fromEmail: viewFor.from_email, date: viewFor.date }} onClose={() => setViewFor(null)} />}
    </div>
  );
}
